// #region Imports
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  createMRTColumnHelper,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_PaginationState as PaginationState,
  type MRT_Row,
  type MRT_RowSelectionState as RowSelectionState,
  type MRT_SortingState as SortingState,
  type MRT_TableInstance,
  type MRT_TableOptions,
} from "material-react-table";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type UIEvent } from "react";
import SearchMatch, { SearchMatchScope } from "_shared/_views/tables/search-match";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { TableSortButton } from "_shared/_views/tables/table-cards";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import TemplateOwnershipFilter, { type TemplateOwnershipScope } from "_shared/_views/templates/template-ownership-filter";
// #endregion

// #region Styles
const TopToolbarActions = styled("div")(({ theme }) => ({
  alignItems: "stretch",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  minWidth: 0,
  width: "100%",
}));
const NameContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  maxWidth: "100%",
  minWidth: 0,
  width: "100%",
}));
/**
 * The line a caller's `content` draws on. A row, because a caller may hand in more than one element — a
 * document's file icon beside its name — and laid one under the other in the column above they would
 * put the icon on a line of its own.
 */
const ContentLine = styled("div")(({ theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(1),
  display: "flex",
  minWidth: 0,
}));
/**
 * The name the reader picks a row by. It may take a second line before it gives way, and the whole
 * name is in its `title` when two are not enough — the rule #6609 set for fee schedule templates, now
 * every panel's.
 */
const RowName = styled(Typography)(() => ({
  display: "-webkit-box",
  fontSize: "1em",
  fontWeight: 500,
  minWidth: 0,
  overflow: "hidden",
  overflowWrap: "anywhere",
  whiteSpace: "normal",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
}));
/**
 * The line under the name: how much of the row is in use, then whatever the caller's `detail` draws
 * (a template's tags). The count sat at the right end of the name's line, where it fought the name for
 * width and was clipped to "3" at the panel's opening width.
 */
const DetailLine = styled("div")(({ theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(1),
  display: "flex",
  minWidth: 0,
  "& > :last-child:not(:first-child)": { flex: "1 1 auto", minWidth: 0 },
}));
/** "2 of 3 in use": quieter than the name, and never the part that gives way. */
const InUseCount = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  flex: "0 0 auto",
  fontSize: "0.75rem",
  whiteSpace: "nowrap",
}));
/**
 * A line of the panel's controls: the ownership toggle and the search it narrows on the first, then the
 * tab strip and the panel's own action on the next. Stacked, the first of these took 76px of a phone's
 * height for a 75px toggle and a box that left the rest of its line empty (#5907), and the wide panel
 * paid the same — a line for the toggle, a line for the search — until it took this layout too.
 *
 * The toggle comes first, so the reader picks All or Mine and then types (#5394). The search wraps
 * under it rather than shrinking past use where the column is too narrow for both.
 */
const ToolbarRow = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  minWidth: 0,
  width: "100%",
  "& > .MuiToggleButtonGroup-root": { flexShrink: 0 },
  "& > .MuiCollapse-root": { flex: "1 1 160px", minWidth: 0 },
  // The tabs never shrink: beside an Upload Template button, full-width tabs split what the button left
  // and clipped Recent to nothing, and with it the way back to Templates (#6035). The action wraps
  // under them instead.
  "& > .MuiTabs-root": { flex: "1 0 auto" },
  "& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner, & .MuiFormControl-root": { minWidth: 0, width: "100%" },
  "& .MuiTabs-root, & .MuiTab-root": { minHeight: 36 },
  "& .MuiTab-root": { paddingBlock: theme.spacing(0.5) },
}));
/**
 * Collapsed into a labelled summary when the surface is narrow, open and headerless when it is not.
 *
 * `compact` is a prop rather than a media query because the panel's surface is not always the
 * window: on a proposal tab it is the page's pane, which the comments and category panels take a
 * column out of. A 1280px display with comments open left this drawn full height and fully expanded
 * in a 608px pane while the viewport query still called the display a wide one (#4964).
 */
const TemplateAccordion = styled(Accordion, {
  shouldForwardProp: (prop) => prop !== "compact",
})<{ compact?: boolean }>(({ compact, theme }) => ({
  backgroundColor: "transparent",
  boxShadow: "none",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minWidth: 0,
  width: "100%",
  "&::before": {
    display: "none",
  },
  "& .MuiAccordionSummary-root": {
    display: "none",
    minHeight: 0,
  },
  "& .MuiAccordionSummary-content": {
    margin: theme.spacing(1, 0),
  },
  "& .MuiAccordionDetails-root": {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    minHeight: 0,
    padding: 0,
  },
  "& .MuiCollapse-root, & .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner, & .MuiAccordion-region": {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    minHeight: 0,
  },
  ...(compact && {
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    height: "auto",
    marginBottom: theme.spacing(1),
    // MUI gives an expanded accordion margins of its own and zeroes the bottom one where it is the
    // last of its kind — which it always is here — so the open panel sat flush on the tab's issue
    // notice while the closed one stood 8px off it (#5907). Doubled to outrank both of MUI's rules.
    "&&.Mui-expanded": {
      marginBottom: theme.spacing(1),
      marginTop: 0,
    },
    overflow: "hidden",
    "& .MuiAccordionSummary-root": {
      display: "flex",
      minHeight: 40,
    },
    "& .MuiAccordionSummary-content": {
      margin: theme.spacing(0.5, 0),
    },
    "& .MuiAccordionDetails-root": {
      flex: "0 0 auto",
      padding: theme.spacing(0, 1, 0.5),
    },
    "& .MuiCollapse-root, & .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner, & .MuiAccordion-region": {
      flex: "0 0 auto",
    },
  }),
}));
/**
 * Under the rows the panel already has, when asking for the next ones failed. The empty state names a
 * failure only where there are no rows, so without this the reader saw the rows they had and nothing
 * else, and scrolling asked for nothing more (review of #6896).
 */
const LoadMoreFailure = styled("div")(({ theme }) => ({
  alignItems: "center",
  borderTop: `1px solid ${theme.palette.divider}`,
  color: theme.palette.error.main,
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "space-between",
  padding: theme.spacing(0.5, 1),
}));
// #endregion

// #region Row model
/**
 * One line of a template panel.
 *
 * The panel is deliberately told about rows rather than about templates. Documents, team templates
 * and fee-schedule templates have nothing in common as domain objects — one is a file, one holds a
 * leader and members, one holds priced categories — and the three panels the owner asked to look
 * alike differ only in what a row is called, what it draws beside the name, and what selecting it
 * does. Everything else, from the ownership toggle down to the last row, is the same panel.
 *
 * A row owns its own answer to being selected. That is what lets the panel stay ignorant of all
 * three domains: it never computes a selection, it reports a press and redraws from whatever the
 * caller says is true next.
 */
export type TemplateSelectorRow = {
  /** Stable within the panel and the same across renders — the expander and the checkbox key off it. */
  id: string;
  /** What the row is called. The sort and the accessible names of its controls read this. */
  name: string;
  /**
   * Drawn in the Name cell in place of the bare name, where a row has more to carry — a file's type
   * glyph and its processing state, a template's tag, the line saying who owns it.
   */
  content?: ReactNode;
  /**
   * Drawn on the line under the name, after the in-use count: a fee schedule template's tags. Kept off
   * the name's line so the name keeps its width.
   */
  detail?: ReactNode;
  /** Whether the row's checkbox is ticked. The caller's own state is the only source of this. */
  selected: boolean;
  /**
   * Ticked through something else: a team template whose people are already on the team without the
   * template itself having been applied, a document a selected template contributed.
   */
  indeterminate?: boolean;
  /**
   * How much of the row is already in play — a fee-schedule template's categories already in the fee
   * schedule, say — drawn as "2 of 3 in use" under the name while the row is `indeterminate`. The dash says
   * "some"; this says how many, which otherwise took opening the row and counting ticks. A row that
   * cannot count its parts leaves it off and keeps the dash alone.
   */
  inUse?: { used: number; total: number };
  /** Greys the row's checkbox out. `disabledReason` is the tooltip that says why. */
  disabled?: boolean;
  disabledReason?: string;
  /** The row's own controls, already built — `RowActions` from `_shared/_views/tables/row-actions`. */
  actions?: ReactNode;
  /** The rows an expander reveals: a team template's people, a fee-schedule template's categories. */
  children?: readonly TemplateSelectorRow[];
  /** Called with what this row's selection should become. */
  onSelectedChanged: (selected: boolean) => void;
};

export type TemplateSelectorTab = {
  /** Reported back through `onTabChanged`. */
  value: string;
  label: string;
};

/** One row's press, for a caller hearing about a whole press at once — see `onSelectionChanged`. */
export type TemplateSelectorSelectionChange = {
  /** The `id` of the `TemplateSelectorRow` that changed. */
  readonly id: string;
  readonly selected: boolean;
};
// #endregion

export type TemplateSelectorProps = {
  className?: string;
  /**
   * Whether the surface drawing this is too narrow to hold the panel open. Defaults to the
   * viewport's `md` breakpoint, which is the right question wherever the panel has the window to
   * itself; a proposal tab passes what its pane measured instead.
   */
  compact?: boolean;
  /**
   * What the panel lists, plural and lower case — "templates", "team templates". The empty state is
   * worded from it, so "No team templates" and "No matching team templates" follow from one phrase
   * rather than being invented per panel.
   */
  subject: string;
  /** What the collapsed summary is called while nothing is selected. */
  collapsedLabel: string;
  /** How many of the caller's own things are selected, for the collapsed summary. */
  selectedCount: number;
  rows: readonly TemplateSelectorRow[];
  /** Whose templates are being shown, and what to call them in the toggle's accessible names. */
  ownership: {
    label: string;
    groupLabel?: string;
    value: TemplateOwnershipScope;
    onChange: (value: TemplateOwnershipScope) => void;
  };
  /**
   * The strip above the rows. Left off where a panel has only one source to draw from: a team
   * template panel has no second list to offer, and a strip with one tab in it says nothing.
   */
  tabs?: readonly TemplateSelectorTab[];
  activeTab?: string;
  onTabChanged?: (value: string) => void;
  /**
   * The search box, controlled rather than left to the table: a panel backed by a paged query has to
   * search on the server and one backed by a list in hand searches in hand. The panel should not
   * have to know which, so it is told the rows it is given are already the right ones.
   */
  search: {
    value: string;
    onChange: (value: string) => void;
  };
  /**
   * How many rows the caller's query asks for, and how many there are. The panel has no pager: it starts
   * at the caller's `pageSize`, and asks for that many more each time the reader scrolls to the last row
   * it has, until it has `rowCount` (#6862). The page index stays 0, so each request is the first
   * `pageSize` rows in the panel's order, and a row never moves between two requests.
   */
  pagination: {
    value: PaginationState;
    onChange: (value: PaginationState) => void;
    rowCount: number;
  };
  /**
   * The order of the rows, which the caller's query applies. The rows arrive a part at a time, so the
   * table cannot sort them: sorting the rows in hand would order twenty of sixty and call it the order,
   * and the next twenty would land in the middle of it. The Name header sets it, A to Z or Z to A.
   */
  sorting: {
    value: SortingState;
    onChange: (value: SortingState) => void;
  };
  /** Where the panel's rows are in flight or have failed, so the empty area can say which. */
  status?: {
    isLoading?: boolean;
    isFetching?: boolean;
    isError?: boolean;
    /**
     * What went wrong, in the API's own words, to stand where the failure state's generic second
     * line would.
     *
     * Take it from the rejected query with `getErrorMessageFromUnknown`, which gives the server's
     * own prose where the response carried Problem Details and the app's standard support sentence
     * where it did not — so `undefined` means there was no rejection to read, and the generic line
     * stands.
     */
    errorMessage?: string;
    onRetry?: () => void;
  };
  /**
   * The panel's own action, already built by the caller — Upload Template on a document panel,
   * Create Template on a team or fee-schedule one. A caller with nothing to offer, or without the
   * permission to offer it, passes nothing and the slot collapses.
   */
  action?: ReactNode;
  /** Anything the panel must keep mounted without drawing — a hidden file input, say. */
  children?: ReactNode;
  /**
   * Every row that changed in one press, together, for a caller that accumulates.
   *
   * A row's own `onSelectedChanged` is told once per row, which a select-all press turns into N
   * calls in one tick. A caller that derives its next state from a value captured at render — which
   * is all of them — then reads the same pre-press value N times and keeps only the last, so a
   * select-all over three rows committed one (#5163). Supply this and the press arrives once, as
   * what changed; the caller folds the lot into one update. `useAddAllColleagues` takes the same
   * shape for the same reason.
   *
   * **Supplying this is what offers select-all at all.** Without it the header checkbox is not
   * drawn, because the per-row contract cannot carry a press that changes more than one row. Rows
   * still report individually through `onSelectedChanged` either way — a single press is a
   * one-element batch, and a caller that reads it as a toggle keeps working.
   */
  onSelectionChanged?: (changes: readonly TemplateSelectorSelectionChange[]) => void;
};

const columnHelper = createMRTColumnHelper<TemplateSelectorRow>();

/** How near the end of the list, in pixels, the reader scrolls before the next rows are asked for. */
const LOAD_MORE_DISTANCE = 120;
/** The list's height inside a compact pane's accordion, about eight rows. */
const COMPACT_LIST_HEIGHT = 360;

const columns = [
  columnHelper.accessor((row: TemplateSelectorRow) => row.name, {
    header: "Name",
    id: "name",
    // The one column a template panel has, so there is nothing to hide, reorder or filter it
    // against — its menu would offer a sort the header already offers and nothing else.
    enableColumnActions: false,
    // The panel's search is its filter, applied by the caller's query. The phone's Filter sheet would
    // offer a second one over the name that nothing applies.
    enableColumnFilter: false,
    sortDescFirst: false,
    grow: 1,
    minSize: 0,
    // A small basis the column grows from, never a width it insists on. At 240 the row was 40 + 240 + 40
    // = 320px in a column that opens at 290, and the expander, now at the row's end, was clipped off it.
    size: 100,
    muiTableBodyCellProps: {
      sx: {
        minWidth: 0,
        overflow: "hidden",
      },
    },
    Cell: renderNameCell,
  }),
];

/**
 * The same column with a kind, for a phone. A kind makes this a list table, which is what draws it as
 * cards there (#7163). Wider, the panel keeps the table without one: a list table fits its columns to
 * their content and stops them growing, which narrowed the one column the panel grows to its width,
 * clipped every name sooner and drew a resize handle a one-column picker has no use for.
 */
const phoneColumns = columns.map((column) => ({ ...column, meta: { ...column.meta, kind: "name" as const } }));

/**
 * The panel a tab picks its templates from.
 *
 * This is the shape the Conflicts and Policies tabs have had all along — an ownership toggle, a tab
 * strip, an upload action, a search box and a table of named rows with checkboxes — lifted out of
 * the document selector so the Vendor Team and Fee Schedule tabs can wear it too (#4955). The owner
 * asked for the Conflicts panel's look on all of them; three copies of it would have made that true
 * this week and false by the next change to any one of them, so there is one.
 *
 * It knows nothing about templates. It is handed rows, and a row says what it is called, whether it
 * is ticked, what it draws and what to do when it is pressed.
 */
export default function TemplateSelector(props: Readonly<TemplateSelectorProps>) {
  const {
    action,
    activeTab,
    children,
    className,
    collapsedLabel,
    onSelectionChanged,
    onTabChanged,
    ownership,
    pagination,
    rows,
    search,
    selectedCount,
    sorting,
    subject,
    tabs,
  } = props;
  const status = props.status;
  const theme = useTheme();
  const viewportIsMdDown = useMediaQuery(theme.breakpoints.down("md"));
  // The query `MRTNoMemo` draws cards under.
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const isCompact = props.compact ?? viewportIsMdDown;
  // Starts collapsed where it collapses at all, which is the fee-schedule panel's behaviour and the
  // reason it has one: the templates are a picker and the tab's own editor is the work. Where the
  // surface is wide enough to hold the panel open this is never read.
  const [panelOpen, setPanelOpen] = useState(false);
  const expanded = !isCompact || panelOpen;

  // Every row in the panel, parents and children alike, by id. A press reaches this component as an
  // id, and walking the tree once per press would be the same walk repeated once per row.
  const rowsById = useMemo(() => {
    const index = new Map<string, TemplateSelectorRow>();
    const visit = (row: TemplateSelectorRow) => {
      index.set(row.id, row);
      for (const child of row.children ?? []) visit(child);
    };
    for (const row of rows) visit(row);
    return index;
  }, [rows]);

  // The table's selection is derived, never held. MRT keeps a record of its own and the caller keeps
  // the truth; deriving one from the other on every render is what stops the two drifting apart —
  // which is the failure the two-way effect between them was written to paper over, complete with a
  // ref to skip the echo.
  const rowSelection = useMemo(() => {
    // Ticked rows only. The table counts the record's keys, not its true values, to tell "some" from
    // "all", so a row recorded as false made every partial selection read as all and the select-all
    // never drew its dash (#7171).
    const selection: RowSelectionState = {};
    for (const [id, row] of rowsById) {
      if (row.selected) selection[id] = true;
    }
    return selection;
  }, [rowsById]);

  const hasChildren = useMemo(() => rows.some((row) => (row.children?.length ?? 0) > 0), [rows]);
  const rowsHaveActions = useMemo(() => [...rowsById.values()].some((row) => Boolean(row.actions)), [rowsById]);

  const handleRowSelectionChange = useCallback(
    (updater: RowSelectionState | ((previous: RowSelectionState) => RowSelectionState)) => {
      const previous: RowSelectionState = {};
      for (const [id, row] of rowsById) previous[id] = row.selected;
      const next = typeof updater === "function" ? updater(previous) : updater;
      // What changed, and never the whole record: MRT hands back every row, and a caller told that
      // a row it already had ticked is ticked would untick it if it reads a report as a toggle —
      // which the template panels do.
      const changes: TemplateSelectorSelectionChange[] = [];
      for (const [id, row] of rowsById) {
        const selected = next[id] ?? false;
        if (selected !== row.selected) changes.push({ id, selected });
      }
      if (changes.length === 0) return;
      // One press, one report, where the caller asked to hear it that way. A select-all is the press
      // that changes more than one row, and N per-row calls in one tick is what no caller holding
      // accumulating state can absorb (#5163).
      if (onSelectionChanged) {
        onSelectionChanged(changes);
        return;
      }
      for (const change of changes) {
        rowsById.get(change.id)?.onSelectedChanged(change.selected);
      }
    },
    [onSelectionChanged, rowsById],
  );

  // The first request's size is the step every later one grows by.
  const [firstPageSize] = useState(pagination.value.pageSize);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const isFetching = Boolean(status?.isFetching) || Boolean(status?.isLoading);
  // The request has more to give, and what it asked for has arrived: while the larger request is in
  // flight the rows are still the smaller one's, and asking again would skip a step.
  const canLoadMore =
    pagination.value.pageSize < pagination.rowCount && rows.length >= pagination.value.pageSize && !isFetching;

  // The rows in hand are the last answer, kept while the larger request failed.
  const loadMoreFailed = Boolean(status?.isError) && rows.length > 0;

  const loadMore = useCallback(() => {
    if (!canLoadMore) return;
    pagination.onChange({ pageIndex: 0, pageSize: pagination.value.pageSize + firstPageSize });
  }, [canLoadMore, firstPageSize, pagination]);

  /** A new search, owner or order is a new list: back to its first rows, and to the top of them. */
  const startAgain = useCallback(() => {
    pagination.onChange({ pageIndex: 0, pageSize: firstPageSize });
    tableContainerRef.current?.scrollTo?.({ top: 0 });
  }, [firstPageSize, pagination]);

  const handleTableScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const { clientHeight, scrollHeight, scrollTop } = event.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < LOAD_MORE_DISTANCE) loadMore();
    },
    [loadMore],
  );

  // Rows that do not fill the column give the reader nothing to scroll, so the next ones are asked for
  // until they do, or until there are no more.
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container || !expanded) return;
    if (container.scrollHeight - container.clientHeight < LOAD_MORE_DISTANCE) loadMore();
  }, [expanded, loadMore, rows]);

  const renderTopToolbarCustomActions = useCallback(
    (toolbarProps: { table: MRT_TableInstance<TemplateSelectorRow> }) => {
      const ownershipFilter = (
        <TemplateOwnershipFilter
          groupLabel={ownership.groupLabel}
          label={ownership.label}
          value={ownership.value}
          onChange={(value) => {
            ownership.onChange(value);
            startAgain();
          }}
        />
      );
      const tabStrip = tabs && tabs.length > 0 && (
        <Tabs
          indicatorColor="primary"
          textColor="primary"
          value={activeTab ?? tabs[0].value}
          variant="standard"
          onChange={(_event, value: string) => onTabChanged?.(value)}
        >
          {tabs.map((tab) => (
            <Tab key={tab.value} label={tab.label} value={tab.value} />
          ))}
        </Tabs>
      );
      // A phone's cards have no header to sort by. Their Sort leads the action's line, which held the
      // action alone at its end, rather than taking a line of its own above the cards (#7163).
      const sortButton = isPhone && <TableSortButton table={toolbarProps.table} variant="text" />;
      // The search leaves MRT's own slot (see `positionGlobalFilter`) for the toggle's line, and the
      // panel's action joins the tab strip's.
      return (
        <TopToolbarActions>
          <ToolbarRow>
            {ownershipFilter}
            <GlobalFilterTextField table={toolbarProps.table} />
          </ToolbarRow>
          {(tabStrip || sortButton || action) && (
            <ToolbarRow sx={tabStrip ? undefined : { justifyContent: sortButton ? "space-between" : "flex-end" }}>
              {tabStrip}
              {sortButton}
              {action}
            </ToolbarRow>
          )}
        </TopToolbarActions>
      );
    },
    [action, activeTab, isPhone, onTabChanged, ownership, startAgain, tabs],
  );

  // An empty fragment rather than `null`. MRT reads this result with `??`, so a nullish return falls
  // straight back to its own Show/Hide-columns, Toggle-filters and Toggle-full-screen buttons — which is
  // how the active Vendor Team panel came to draw four icon buttons the Conflicts panel it is meant to
  // match has never had (#4955). This is a one-column picker; there is nothing to hide, filter or
  // magnify, and the panel's own action is on the tab strip's line.
  const renderToolbarInternalActions = useCallback(() => <></>, []);

  const renderRowActions = useCallback(
    (params: { row: MRT_Row<TemplateSelectorRow> }) => params.row.original.actions ?? null,
    [],
  );

  const muiTableBodyRowProps = useCallback(
    (params: { row: MRT_Row<TemplateSelectorRow> }) => {
      const row = params.row.original;
      // Only a ticked row is tinted, with the table's own selected highlight — the one every other
      // selectable table in the app draws. A partly used row took a fainter wash of the same colour,
      // and beside a ticked one the two read as one blue: a column of applied and part-used templates
      // looked all selected. The checkbox's dash and the "N of M in use" line say "some" instead.
      return {
        selected: row.selected,
        sx: {
          cursor: row.disabled ? "default" : "pointer",
        },
        onClick: () => {
          if (!row.disabled) row.onSelectedChanged(!row.selected);
        },
      };
    },
    [],
  );

  const muiSelectCheckboxProps = useCallback((params: { row: MRT_Row<TemplateSelectorRow> }) => {
    const row = params.row.original;
    return {
      checked: row.selected,
      indeterminate: !row.selected && Boolean(row.indeterminate),
      title: row.disabled ? row.disabledReason : undefined,
    };
  }, []);

  // Built inline rather than memoized. Half of what goes in here is an object the caller allocated
  // this render — `search`, `pagination`, `ownership` — so a memo keyed on them would rebuild every
  // time anyway, and `MRTNoMemo` is opted out of memoization in any case.
  const tableOptions: MRT_TableOptions<TemplateSelectorRow> = {
    columns: isPhone ? phoneColumns : columns,
    data: rows as TemplateSelectorRow[],
    getRowId: (row) => row.id,
    getSubRows: (row) => row.children as TemplateSelectorRow[] | undefined,
    enableExpanding: hasChildren,
    // A parent's press is the caller's to interpret — applying a team template is not the same act
    // as ticking each of its people — so MRT is told not to cascade one into the other.
    enableSubRowSelection: false,
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableColumnPinning: false,
    enableFacetedValues: true,
    enableGrouping: false,
    // The actions column costs 80px of the name's width, so it is drawn only where a row has controls
    // to put in it. Drawn empty, it pushed the in-use count past the panel's edge (#6862).
    enableRowActions: rowsHaveActions,
    enableRowSelection: (row) => !row.original.disabled,
    // Offered only to a caller that can hear a whole press at once. `enableRowSelection` being a
    // function is enough to draw the header checkbox, so leaving this to its default put a
    // select-all on all six panels that committed one row of N (#5163); the panels whose ticks mean
    // "apply this template" have no batch handler and no defined meaning for applying ten at once,
    // so they no longer offer it.
    enableSelectAll: Boolean(onSelectionChanged),
    enableSorting: true,
    enableStickyFooter: true,
    enableStickyHeader: true,
    // The header is how the reader sorts by name, so every panel draws it.
    enableTableHead: true,
    // Name is sorted one way or the other, never not at all: the server always applies an order.
    enableSortingRemoval: false,
    // Scrolling to the last row asks for more, so there is no pager to draw (#6862). The foot is drawn
    // only to offer the next rows again after they failed to load.
    enablePagination: false,
    enableBottomToolbar: loadMoreFailed,
    renderBottomToolbar: () => (
      <LoadMoreFailure role="alert">
        <Typography variant="body2">{`More ${subject} did not load.`}</Typography>
        {status?.onRetry && (
          <Button size="small" onClick={status.onRetry}>
            Retry
          </Button>
        )}
      </LoadMoreFailure>
    ),
    displayColumnDefOptions: {
      "mrt-row-actions": { grow: 0, maxSize: 80, minSize: 80, size: 80 },
      /**
       * The expander holds one 28px chevron — the same size control as the checkbox beside it — so it
       * is given the same 40px the checkbox gets. Left alone, MRT sizes this column at 100px, which is
       * its default for an expander over sub-rows with no detail panel; in a panel that opens at its
       * 375px minimum that is 27% of the panel spent on a chevron, and it comes out of `Name`, the one
       * column with something to read in it.
       */
      "mrt-row-expand": { grow: 0, maxSize: 40, minSize: 40, size: 40 },
      "mrt-row-select": { grow: 0, maxSize: 40, minSize: 40, size: 40 },
    },
    initialState: {
      density: "compact",
      showGlobalFilter: true,
    },
    layoutMode: "grid",
    positionActionsColumn: "last",
    // The checkbox is the row's control and leads it; the chevron that opens the row trails, so the
    // name starts 40px nearer the edge.
    positionExpandColumn: "last",
    positionToolbarAlertBanner: "none",
    // The search is drawn beside the ownership toggle by `renderTopToolbarCustomActions`.
    positionGlobalFilter: "none",
    // The rows handed in are already the right ones: a panel backed by a paged query filtered and
    // paged them on the server, one backed by a list in hand did it in hand. Either way the table is
    // the renderer here, not the thing deciding what to show.
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    muiTopToolbarProps: {
      sx: {
        containerType: "inline-size",
        "& > .MuiBox-root": {
          "@container (min-width: 800px)": {
            alignItems: "flex-start",
            flexDirection: "row",
            paddingLeft: 3,
          },
          boxSizing: "border-box",
          gap: 1,
          justifyContent: "space-between",
          mt: 1,
          // The ownership toggle over the search box, in the order MRT draws them. The search narrows
          // whatever the toggle chose, so the reader picks All or Mine first and then types (#5394);
          // `column-reverse` had put the box above the thing it filters.
          flexDirection: "column",
          paddingLeft: 0,
          paddingTop: 0,
          width: "100%",
          "& > .MuiBox-root": {
            flex: "0 1 auto",
            minWidth: 0,
          },
          // Everything is in the first box, and the empty second one would cost a gap.
          "& > .MuiBox-root:last-of-type:empty": { display: "none" },
          "& > :first-child": {
            "@container (min-width: 800px)": {
              flex: "1 1 50%",
              pl: 0,
              width: "auto",
            },
            boxSizing: "border-box",
            pl: { xs: 0, md: 1 },
          },
          "& > .MuiBox-root:last-of-type": {
            "@container (min-width: 800px)": {
              flex: "1 1 50%",
              justifyContent: "flex-end",
            },
            alignItems: "center",
            boxSizing: "border-box",
            flex: 1,
            gap: 1,
            justifyContent: "space-between",
            minWidth: 0,
            pl: { xs: 0, md: 1 },
            width: "100%",
          },
        },
        width: "100%",
      },
    },
    muiTableContainerProps: {
      ref: tableContainerRef,
      onScroll: handleTableScroll,
      sx: {
        flex: { xs: "0 0 auto", md: 1 },
        minHeight: 0,
        overflowX: "hidden",
        // Folded into a compact pane the column has no height of its own to scroll within, so the list
        // takes one, and scrolls there rather than the whole page growing a template at a time.
        ...(isCompact && { maxHeight: COMPACT_LIST_HEIGHT, overflowY: "auto" }),
      },
    },
    muiTableHeadCellProps: {
      sx: {
        px: { xs: 0.5, md: 1 },
        "& .Mui-TableHeadCell-Content": { minWidth: 0 },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        minWidth: 0,
        px: { xs: 0.5, md: 1 },
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        display: "flex",
        flexDirection: "column",
        height: { xs: "auto", md: "100%" },
        width: "100%",
        // A phone's cards scroll in a list of their own, which a compact pane bounds as it bounds the
        // table's container. Unbounded, the list never scrolls, and the rows asked for until they fill it
        // would be every row there is.
        ...(isCompact && { "& > ul": { flex: "0 1 auto", maxHeight: COMPACT_LIST_HEIGHT, minHeight: 0 } }),
      },
    },
    muiSearchTextFieldProps: {
      placeholder: "Search",
      // No `slots.root` here on purpose. `MRTNoMemo` fills that slot with `MrtTextFieldRoot` — a
      // `FormControl` that drops the legacy `inputProps`/`InputProps` MRT still passes — and naming
      // a plain `FormControl` instead, which the document selector used to, put `inputProps` onto a
      // DOM node and made React complain about it on every render of the panel.
      sx: {
        flex: { xs: "1 1 auto", md: "0 1 auto" },
        minWidth: 0,
        width: { xs: "100%", md: "auto" },
      },
      slotProps: {
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: search.value ? (
            <InputAdornment position="end">
              <IconButton aria-label="Clear search" size="small" onClick={() => search.onChange("")}>
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        },
      },
    },
    // No `muiToolbarAlertBannerProps` and no `showAlertBanner`. Both were set here, and both were
    // dead: `positionToolbarAlertBanner` is "none" a few lines up, so MRT draws no banner at all.
    // The four words they carried — "Error loading data" — are the ones #5235 reports a refused
    // reviewer seeing, and nobody has ever seen them from this panel. What a failing panel
    // actually shows is `renderEmptyRowsFallback` below, which is where the failure is named.
    //
    // The position stays "none". That banner is also where MRT reports how many rows are selected,
    // and selecting rows is the whole point of a template picker: turning it on for the sake of an
    // error would put a running "1 of 12 rows selected" above every panel in the app.
    muiSelectCheckboxProps,
    muiTableBodyRowProps,
    renderEmptyRowsFallback: emptyRowsFallback<TemplateSelectorRow>({
      errorDescription: status?.errorMessage,
      isError: status?.isError,
      isLoading: status?.isFetching,
      onRetry: status?.onRetry,
      subject,
    }),
    renderRowActions,
    renderToolbarInternalActions,
    renderTopToolbarCustomActions,
    onGlobalFilterChange: (updater) => {
      const next = typeof updater === "function" ? String(updater(search.value) ?? "") : String(updater ?? "");
      if (next === search.value) return;
      search.onChange(next);
      startAgain();
    },
    onRowSelectionChange: handleRowSelectionChange,
    onSortingChange: (updater: SortingState | ((previous: SortingState) => SortingState)) => {
      sorting.onChange(typeof updater === "function" ? updater(sorting.value) : updater);
      startAgain();
    },
    rowCount: pagination.rowCount,
    state: {
      globalFilter: search.value,
      isLoading: status?.isLoading,
      rowSelection,
      showProgressBars: Boolean(status?.isFetching) && !status?.isLoading,
      sorting: sorting.value,
    },
  };

  return (
    <TemplateAccordion
      className={className}
      compact={isCompact}
      elevation={0}
      expanded={expanded}
      onChange={(_event, isExpanded) => {
        if (isCompact) setPanelOpen(isExpanded);
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
        <Typography component="span" color="primary">
          {selectedCount > 0 ? `${selectedCount} Selected` : collapsedLabel}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {children}
        {/* The panel's search, lent to the `content` each caller drew before this table existed (#6590). */}
        <SearchMatchScope term={search.value}>
          {/* A tick is the only thing a row is for, so on a phone each card ticks on a tap (#7163). */}
          <MRTNoMemo<TemplateSelectorRow> options={tableOptions} cards={{ sortInToolbar: true, ticks: true }} />
        </SearchMatchScope>
      </AccordionDetails>
    </TemplateAccordion>
  );
}

/**
 * A row's name, or the `content` its caller drew in its place. Every panel's search reads the name, so
 * the bare name marks what was searched for (#6590); `content` is the caller's to mark.
 */
function renderNameCell({
  row,
  table,
}: Readonly<{ row: MRT_Row<TemplateSelectorRow>; table: MRT_TableInstance<TemplateSelectorRow> }>) {
  const { content, detail, inUse, indeterminate, name, selected } = row.original;
  const showInUse = Boolean(inUse && indeterminate && !selected);
  return (
    <NameContainer>
      {content ? (
        <ContentLine>{content}</ContentLine>
      ) : (
        <RowName title={name}>
          <SearchMatch table={table} text={name} />
        </RowName>
      )}
      {(showInUse || detail) && (
        <DetailLine>
          {showInUse && inUse && <InUseCount>{`${inUse.used} of ${inUse.total} in use`}</InUseCount>}
          {detail}
        </DetailLine>
      )}
    </NameContainer>
  );
}
